import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Captions, CalendarClock, Clapperboard, Film, Home, LogOut, PanelLeftClose, PanelLeftOpen, PenTool, Plus, Scissors, Shield, Sparkles, User, Users, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { useLocale } from '../../i18n/LocaleContext';
import { pathFor } from '../../i18n/routes';
import { useUsageStore, selectUsageState } from '../../stores/usageStore';
import { track } from '../../analytics/track';
import useSceneStudio from '../scenes/useSceneStudio';
import { STUDIO_MODES, studioModeOf, ModeItems } from '../AIAgent/StudioShell';
import ThemeToggle from '../ThemeToggle';
import { useSidebar } from './SidebarContext';

const TOOLS = [
  { key: 'aiClipping', to: '/ai-clipping', Icon: Scissors, match: /^\/ai-clipping/ },
  { key: 'captions', to: '/auto-caption', Icon: Captions, match: /^\/auto-caption/ },
  { key: 'reframe', to: '/video-reframe', Icon: Film, match: /^\/video-reframe/ },
  { key: 'editor', to: '/editor', Icon: PenTool, match: /^\/editor/ },
];
const MODE_ICONS = { video: Sparkles, scenes: Clapperboard, series: CalendarClock, characters: Users };

function Item({ to, Icon, label, active, rail, end, onClick, badge }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      title={rail ? label : undefined}
      className={cn(
        'flex items-center gap-3 rounded-xl text-[14px] transition-colors',
        rail ? 'h-9 w-10 justify-center' : 'h-9 px-3',
        active ? 'bg-ink/[0.07] font-semibold text-ink' : 'text-ink-3 hover:bg-ink/[0.05] hover:text-ink',
      )}
    >
      <Icon className={cn('h-[18px] w-[18px] shrink-0', active && 'text-brand')} aria-hidden="true" />
      <span className={cn('min-w-0 flex-1 truncate', rail && 'sr-only')}>{label}</span>
      {badge && !rail && <span className="rounded-full bg-brand/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-brand">{badge}</span>}
    </NavLink>
  );
}

function Heading({ children, rail }) {
  if (rail) return <div className="mx-2 my-2 border-t border-ink/[0.08]" aria-hidden="true" />;
  return <p className="mb-1 mt-4 px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-3">{children}</p>;
}

function Bar({ used, limit, label }) {
  const unlimited = limit === 'Unlimited' || limit == null;
  const pct = unlimited ? 0 : Math.min(100, (100 * used) / Math.max(1, limit));
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-[12px]">
        <span className="text-ink-3">{label}</span>
        <span className="font-medium tabular-nums text-ink">{unlimited ? '∞' : `${used}/${limit}`}</span>
      </div>
      {!unlimited && (
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-ink/10">
          <div className={cn('h-full rounded-full', pct >= 90 ? 'bg-destructive' : 'bg-brand')} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

/** The plan and what is left of it this month, with the way up. */
function PlanCard({ rail }) {
  const { t, locale } = useLocale();
  const { isProPlan, subscription } = useSubscription();
  const { metrics } = useUsageStore(selectUsageState);
  const minutes = metrics?.video_processing_minutes;
  const videos = metrics?.ai_agent_projects;
  const planName = subscription?.plan?.name || (isProPlan ? 'Pro' : t('pricing.freeName'));
  if (rail) {
    return (
      <Link to={pathFor('pricing', locale)} title={planName} className="flex h-10 w-10 items-center justify-center rounded-xl text-ink-3 hover:bg-ink/[0.05] hover:text-ink">
        <Sparkles className="h-[18px] w-[18px]" aria-hidden="true" />
        <span className="sr-only">{planName}</span>
      </Link>
    );
  }
  return (
    <div className="rounded-2xl border border-ink/[0.1] bg-surface p-3 shadow-[var(--shadow-card)]" data-testid="sidebar-plan">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-semibold text-ink">{planName}</span>
        {isProPlan && <span className="rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-bold uppercase text-brand">Pro</span>}
      </div>
      <div className="mt-3 space-y-2.5">
        {minutes && <Bar used={typeof minutes.used === 'number' ? minutes.used.toFixed(1) : 0} limit={minutes.limit} label={t('nav.videoMinutes')} />}
        {videos && <Bar used={videos.used} limit={videos.limit} label={t('nav.aiAgentVideos')} />}
      </div>
      {isProPlan ? (
        <Link to="/account" className="mt-2.5 flex h-8 items-center justify-center rounded-full border border-ink/15 text-[13px] font-medium text-ink hover:bg-ink/5">{t('sidebar.managePlan')}</Link>
      ) : (
        <Link to={pathFor('pricing', locale)} onClick={() => track('sidebar_upgrade')} className="mt-2.5 flex h-8 items-center justify-center rounded-full bg-ink text-[13px] font-semibold text-page hover:bg-ink/90">{t('nav.upgrade')}</Link>
      )}
    </div>
  );
}

function AccountRow({ rail }) {
  const { t } = useLocale();
  const { user, logout } = useAuth();
  const initial = user?.email?.charAt(0).toUpperCase() || 'U';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="sidebar-account"
          aria-label={t('nav.myAccount')}
          className={cn('flex items-center gap-2.5 rounded-xl text-start transition-colors hover:bg-ink/[0.05]', rail ? 'h-10 w-10 justify-center' : 'h-11 w-full px-2')}
        >
          <Avatar className="h-7 w-7">
            <AvatarImage src={user?.profile_picture_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(initial)}`} alt="" />
            <AvatarFallback>{initial}</AvatarFallback>
          </Avatar>
          {!rail && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium text-ink">{user?.full_name || t('nav.user')}</span>
                <span className="block truncate text-[11px] text-ink-3">{user?.email || ''}</span>
              </span>
              <ChevronsUpDown className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-64 rounded-xl p-1.5">
        <DropdownMenuItem asChild className="gap-2.5 rounded-lg px-2.5 py-2"><Link to="/account"><User className="h-4 w-4" aria-hidden="true" />{t('nav.myAccount')}</Link></DropdownMenuItem>
        {user?.is_admin && <DropdownMenuItem asChild className="gap-2.5 rounded-lg px-2.5 py-2"><Link to="/admin"><Shield className="h-4 w-4" aria-hidden="true" />{t('nav.admin')}</Link></DropdownMenuItem>}
        <DropdownMenuSeparator />
        {/* The three choices on a row of their own: beside the label they ran past the menu's edge */}
        <div className="px-2.5 py-2">
          <span className="block text-[12px] text-ink-3">{t('nav.theme')}</span>
          <ThemeToggle compact className="mt-1.5 w-full justify-between" />
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout()} className="gap-2.5 rounded-lg px-2.5 py-2 text-destructive focus:text-destructive"><LogOut className="h-4 w-4" aria-hidden="true" />{t('nav.logout')}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The signed-in app's sidebar: Home, what the AI Agent makes (with what was made in the
 * open mode, as the studio's rail showed it), the tools that start from a video, and at the
 * bottom the plan with its month and the account. Folds to a rail of icons.
 */
export default function AppSidebar() {
  const { mode, toggle } = useSidebar();
  const { t } = useLocale();
  const { pathname } = useLocation();
  const { scenesOn } = useSceneStudio();
  if (mode === 'hidden') return null;
  const rail = mode === 'rail';
  const inStudio = /^\/ai-agent(\/|$)/.test(pathname);
  const activeMode = inStudio ? studioModeOf(pathname) : null;
  const modes = STUDIO_MODES.filter((m) => m.key !== 'scenes' || scenesOn);

  return (
    <aside
      data-testid="app-sidebar"
      data-mode={mode}
      className={cn('fixed bottom-0 start-0 top-16 z-40 hidden flex-col border-e border-ink/[0.08] bg-page lg:flex', rail ? 'w-16 items-center' : 'w-60')}
      style={{ paddingBottom: 'var(--consent-bar-h, 0px)' }}
    >
      <div className={cn('flex shrink-0 items-center px-3 pt-3', rail ? 'justify-center' : 'justify-end')}>
        <button
          type="button"
          onClick={toggle}
          aria-label={t(rail ? 'sidebar.expand' : 'sidebar.collapse')}
          title={t(rail ? 'sidebar.expand' : 'sidebar.collapse')}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-3 hover:bg-ink/[0.05] hover:text-ink"
        >
          {rail ? <PanelLeftOpen className="h-[18px] w-[18px]" aria-hidden="true" /> : <PanelLeftClose className="h-[18px] w-[18px]" aria-hidden="true" />}
        </button>
      </div>

      <nav aria-label={t('sidebar.label')} className={cn('min-h-0 flex-1 overflow-y-auto overflow-x-hidden', rail ? 'px-3 py-2' : 'px-3 py-2')}>
        <div className={cn('space-y-0.5', rail && 'flex flex-col items-center')}>
          <Item to="/" end Icon={Home} label={t('sidebar.home')} active={pathname === '/'} rail={rail} />
        </div>

        <Heading rail={rail}>{t('sidebar.create')}</Heading>
        <div className={cn('space-y-0.5', rail && 'flex flex-col items-center')} data-testid="sidebar-studio">
          {modes.map(({ key, to, create }) => {
            const active = activeMode === key;
            return (
              <div key={key}>
                <Item to={to} end={key === 'video'} Icon={MODE_ICONS[key]} label={t(`studio.modes.${key}.name`)} active={active} rail={rail} onClick={() => track('studio_rail_mode', { mode: key })} />
                {active && !rail && (
                  <div className="mb-1 ms-4 border-s border-ink/[0.08] ps-2 pt-1">
                    <Link
                      to={create}
                      state={{ newItem: Date.now() }}
                      onClick={() => track('studio_rail_new', { mode: key })}
                      className="flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] font-medium text-ink hover:bg-ink/[0.05]"
                      data-testid={`rail-new-${key}`}
                    >
                      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                      {t(`studio.modes.${key}.new`)}
                    </Link>
                    <ModeItems mode={key} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <Heading rail={rail}>{t('sidebar.fromVideo')}</Heading>
        <div className={cn('space-y-0.5', rail && 'flex flex-col items-center')}>
          {TOOLS.map(({ key, to, Icon, match }) => (
            <Item key={key} to={to} Icon={Icon} label={t(`tools.${key}`)} active={match.test(pathname)} rail={rail} badge={key === 'editor' ? t('nav.badgeBeta') : undefined} />
          ))}
        </div>
      </nav>

      <div className={cn('shrink-0 border-t border-ink/[0.08]', rail ? 'flex flex-col items-center gap-1 p-3' : 'space-y-2 p-3')}>
        <PlanCard rail={rail} />
        <AccountRow rail={rail} />
      </div>
    </aside>
  );
}
